import { range } from 'lodash';
import React from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useSet } from 'react-use';
import { Accordion } from '../../../../../../../CommonComponents/OPL/Accordion/Accordion/Accordion';
import { AccordionButton } from '../../../../../../../CommonComponents/OPL/Accordion/AccordionButton/AccordionButton';
import { Box } from '../../../../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../../../../CommonComponents/OPL/Button/Button';
import { Checkbox } from '../../../../../../../CommonComponents/OPL/Checkbox/Checkbox';
import { IconButton } from '../../../../../../../CommonComponents/OPL/IconButton/IconButton';
import { InputNumberMask } from '../../../../../../../CommonComponents/OPL/Input/InputNumberMask';
import { Select } from '../../../../../../../CommonComponents/OPL/Select/Select/Select';
import { RealEstateSaleOptions } from '../../../../api/useWhatIfOptionsQuery';
import { StyledAccordionPanel } from '../../WhatIf.styles';
import { DEFAULT_SALE, FormValues } from './RealEstateSale.meta';

interface BusinessSaleProps {
  options: RealEstateSaleOptions;
}

export const RealEstateSale = ({ options }: BusinessSaleProps) => {
  const [, { has: isSaleCollapsed, toggle: toggleSale }] = useSet<string>();

  const assetOptions = options.Assets.map(opt => ({ label: opt.Label, value: opt.AssetId }));
  const earmarkOptions = options.Earmarks.map(opt => ({ label: opt.Label, value: opt.Value }));

  const {
    register,
    control,
    watch,
    formState: { errors },
  } = useFormContext<FormValues>();

  const { fields, append: appendSale, remove: removeSale } = useFieldArray({
    control,
    name: 'Sales',
  });

  const watchSales = watch('Sales');

  const sales = fields.map((field, index) => ({
    ...field,
    ...watchSales[index],
  }));

  return (
    <>
      <Accordion isActive={false} css="border: none; border-radius: 0; ">
        {sales.map((sale, index) => {
          const saleYears = options.Assets.find(opt => opt.AssetId === sale.AssetId)?.SaleYears;
          const hasCurrentSale =
            options.Assets.find(opt => opt.AssetId === sale.AssetId)?.HasCurrentPlanSale ?? false;

          const yearsOptions = saleYears && [
            { label: 'At retirement', value: null },
            ...range(saleYears.From, saleYears.To).map(year => ({
              label: year.toString(),
              value: year,
            })),
          ];

          return (
            <React.Fragment key={sale.id}>
              <AccordionButton
                id={sale.id}
                panelId={sale.id}
                isActive={!isSaleCollapsed(sale.id)}
                onClick={() => toggleSale(sale.id)}
                extra={
                  sales.length > 1 && (
                    <IconButton
                      variant="subtleInactive"
                      icon="fal fa-trash-alt"
                      onClick={() => removeSale(index)}
                    />
                  )
                }
              >
                {assetOptions.find(option => option.value === sale.AssetId)?.label ??
                  `Real Estate Asset ${index + 1}`}
              </AccordionButton>

              <StyledAccordionPanel
                id={sale.id}
                buttonId={sale.id}
                isActive={!isSaleCollapsed(sale.id)}
              >
                <Box display="flex" flexDirection="column" gridGap="16" px="16" pb="32">
                  <Controller
                    name={`Sales.${index}.AssetId`}
                    control={control}
                    render={({ field: { value, onChange, ref, ...field } }) => (
                      <Select
                        label="Select Real Estate Asset"
                        formVariant={errors.Sales?.[index]?.AssetId && 'error'}
                        helperText={errors.Sales?.[index]?.AssetId?.message}
                        options={assetOptions}
                        value={assetOptions.find(option => option.value === value)}
                        onChange={option => {
                          onChange(option?.value);
                        }}
                        innerRef={ref}
                        isPortal
                        disabled={sale.DoNotSell}
                        {...field}
                      />
                    )}
                  />

                  <Controller
                    name={`Sales.${index}.SaleYear`}
                    control={control}
                    render={({ field: { value, onChange, ref, ...field } }) => (
                      <Select
                        label="Sale Year"
                        formVariant={errors.Sales?.[index]?.SaleYear && 'error'}
                        helperText={errors.Sales?.[index]?.SaleYear?.message}
                        options={yearsOptions}
                        value={yearsOptions?.find(option => option.value === value)}
                        onChange={option => {
                          onChange(option?.value);
                        }}
                        innerRef={ref}
                        isPortal
                        disabled={!yearsOptions || sale.DoNotSell}
                        {...field}
                      />
                    )}
                  />

                  <Controller
                    control={control}
                    name={`Sales.${index}.SaleValue`}
                    render={({ field: { onChange, ...field } }) => (
                      <InputNumberMask
                        label="Net Proceeds from Sale"
                        decimalScale={2}
                        allowNegative={false}
                        leftIcon="far fa-dollar-sign"
                        formVariant={errors.Sales?.[index]?.SaleValue && 'error'}
                        helperText={errors.Sales?.[index]?.SaleValue?.message}
                        onValueChange={({ value }) => {
                          onChange(value);
                        }}
                        disabled={sale.DoNotSell}
                        {...field}
                      />
                    )}
                  />

                  <Controller
                    name={`Sales.${index}.Earmarks`}
                    control={control}
                    render={({ field: { ref, onChange, value, ...field } }) => (
                      <Select
                        label="Asset Earmark"
                        options={earmarkOptions}
                        value={earmarkOptions.filter(opt =>
                          value.some(
                            v =>
                              v.EarmarkType === opt.value.EarmarkType &&
                              v.Dependent?.Id === opt.value.Dependent?.Id &&
                              v.Name === opt.value.Name,
                          ),
                        )}
                        onChange={opts => onChange(opts.map(opt => opt.value))}
                        innerRef={ref}
                        isMulti
                        isPortal
                        disabled={sale.DoNotSell}
                        {...field}
                      />
                    )}
                  />

                  {hasCurrentSale && (
                    <Checkbox
                      {...register(`Sales.${index}.DoNotSell`)}
                      disabled={sale.AssetId === null}
                    >
                      What if I choose not to sell this asset?
                    </Checkbox>
                  )}
                </Box>
              </StyledAccordionPanel>
            </React.Fragment>
          );
        })}
      </Accordion>

      {sales.length < assetOptions.length && (
        <Button
          variant="subtleActive"
          leftIcon="fas fa-plus"
          onClick={() => appendSale(DEFAULT_SALE)}
          my="24"
          ml="8"
        >
          Add another real estate asset sale
        </Button>
      )}
    </>
  );
};
